/**
 * Contrato de hash e política de senha do Baiocchi Leads (RNF-10, RNF-14, RN-05).
 *
 * Definição canônica. `scripts/lib/senha.ts` reexporta daqui — o hash gerado
 * pelo seed precisa validar no login sem nenhum ajuste (decisão A8 de
 * `01-architecture.md`).
 *
 * Alterar `PARAMETROS_ARGON2` invalida todos os hashes já gravados. Se algum dia
 * for necessário (aumento de custo), o caminho é reidratar no próximo login
 * bem-sucedido, nunca trocar os parâmetros de uma vez.
 */
import { hash, verify, Algorithm } from '@node-rs/argon2'

/** Parâmetros fixos. Mínimo recomendado pelo OWASP para Argon2id. */
export const PARAMETROS_ARGON2 = {
  algorithm: Algorithm.Argon2id,
  memoryCost: 19456, // 19 MiB
  timeCost: 2,
  parallelism: 1,
} as const

export function gerarHashSenha(senhaEmClaro: string): Promise<string> {
  return hash(senhaEmClaro, PARAMETROS_ARGON2)
}

/**
 * Nunca lança: hash corrompido ou de outro algoritmo devolve `false`. Um throw
 * aqui viraria 500 e diferenciaria "hash estranho" de "senha errada" para quem
 * está sondando o login.
 */
export async function conferirSenha(
  hashArmazenado: string,
  senhaEmClaro: string,
): Promise<boolean> {
  try {
    return await verify(hashArmazenado, senhaEmClaro, PARAMETROS_ARGON2)
  } catch {
    return false
  }
}

// -----------------------------------------------------------------------------
// Política de senha — RNF-14 / RN-05
// -----------------------------------------------------------------------------

export const SENHA_MIN_CARACTERES = 10
export const SENHA_MAX_CARACTERES = 200

export type CodigoViolacaoSenha =
  | 'MIN_CARACTERES'
  | 'MAX_CARACTERES'
  | 'SEM_LETRA'
  | 'SEM_NUMERO'
  | 'SENHA_COMUM'
  | 'SEQUENCIA_OBVIA'
  | 'CONTEM_DADOS_DA_CONTA'

export interface ViolacaoSenha {
  readonly codigo: CodigoViolacaoSenha
  readonly mensagem: string
}

/**
 * Senhas notoriamente fracas (RNF-14). Lista curta e deliberadamente enviesada
 * para o teclado brasileiro — é o que aparece de fato. Não substitui uma
 * verificação contra vazamentos (ver docs/security/02, "falta para produção").
 */
const SENHAS_COMUNS: ReadonlySet<string> = new Set([
  '123456',
  '1234567',
  '12345678',
  '123456789',
  '1234567890',
  '12345678910',
  'senha123',
  'senha1234',
  'minhasenha',
  'senhasegura',
  'password',
  'password1',
  'password123',
  'qwerty123',
  'abc123456',
  'admin123',
  'administrador',
  'baiocchi',
  'baiocchi123',
  'baiocchiimoveis',
  'imoveis123',
  'corretor123',
  'brasil123',
  'saopaulo123',
  'goiania123',
  'flamengo123',
  'iloveyou1',
  'primeiro123',
  'mudar123',
  'trocar123',
  'teste123',
  'teste1234',
])

/** Contexto opcional: impede senha derivada do próprio cadastro. */
export interface ContextoSenha {
  readonly email?: string | null
  readonly nome?: string | null
}

function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
}

/** Detecta `123456`, `abcdef`, `aaaaaa` e o teclado (`qwerty`, `asdfgh`). */
function temSequenciaObvia(senha: string): boolean {
  const s = normalizar(senha)

  if (/(.)\1{4,}/.test(s)) return true

  const FILEIRAS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm', '1234567890']
  for (const fileira of FILEIRAS) {
    for (let i = 0; i + 6 <= fileira.length; i++) {
      const trecho = fileira.slice(i, i + 6)
      if (s.includes(trecho)) return true
      if (s.includes([...trecho].reverse().join(''))) return true
    }
  }

  let crescente = 1
  let decrescente = 1
  for (let i = 1; i < s.length; i++) {
    const passo = s.charCodeAt(i) - s.charCodeAt(i - 1)
    crescente = passo === 1 ? crescente + 1 : 1
    decrescente = passo === -1 ? decrescente + 1 : 1
    if (crescente >= 6 || decrescente >= 6) return true
  }

  return false
}

function contemDadosDaConta(senha: string, contexto: ContextoSenha): boolean {
  const s = normalizar(senha)

  const parteLocal = contexto.email ? normalizar(contexto.email).split('@')[0] : undefined
  if (parteLocal && parteLocal.length >= 4 && s.includes(parteLocal)) return true

  if (contexto.nome) {
    for (const parte of normalizar(contexto.nome).split(/\s+/)) {
      if (parte.length >= 4 && s.includes(parte)) return true
    }
  }

  return false
}

/**
 * Todas as violações da senha, para que o formulário mostre a lista inteira de
 * uma vez em vez de um erro por vez (RNF-115).
 */
export function violacoesDaSenha(
  senha: string,
  contexto: ContextoSenha = {},
): ViolacaoSenha[] {
  const violacoes: ViolacaoSenha[] = []

  if (senha.length < SENHA_MIN_CARACTERES) {
    violacoes.push({
      codigo: 'MIN_CARACTERES',
      mensagem: `A senha precisa ter ao menos ${SENHA_MIN_CARACTERES} caracteres.`,
    })
  }
  if (senha.length > SENHA_MAX_CARACTERES) {
    violacoes.push({
      codigo: 'MAX_CARACTERES',
      mensagem: `A senha pode ter no máximo ${SENHA_MAX_CARACTERES} caracteres.`,
    })
  }
  if (!/[a-zA-Z]/.test(senha)) {
    violacoes.push({ codigo: 'SEM_LETRA', mensagem: 'A senha precisa ter ao menos uma letra.' })
  }
  if (!/[0-9]/.test(senha)) {
    violacoes.push({ codigo: 'SEM_NUMERO', mensagem: 'A senha precisa ter ao menos um número.' })
  }
  if (SENHAS_COMUNS.has(normalizar(senha))) {
    violacoes.push({
      codigo: 'SENHA_COMUM',
      mensagem: 'Esta senha é muito conhecida. Escolha outra.',
    })
  }
  if (temSequenciaObvia(senha)) {
    violacoes.push({
      codigo: 'SEQUENCIA_OBVIA',
      mensagem: 'Evite sequências óbvias como "123456", "abcdef" ou "qwerty".',
    })
  }
  if (contemDadosDaConta(senha, contexto)) {
    violacoes.push({
      codigo: 'CONTEM_DADOS_DA_CONTA',
      mensagem: 'A senha não pode conter seu nome nem seu e-mail.',
    })
  }

  return violacoes
}

/**
 * Mesma política, só as mensagens. Formato consumido pelo seed e por CLIs.
 * Lista vazia significa senha aceita.
 */
export function validarPoliticaSenha(
  senha: string,
  contexto: ContextoSenha = {},
): string[] {
  return violacoesDaSenha(senha, contexto).map((violacao) => violacao.mensagem)
}
